import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Server, Sparkles, FolderGit2, Check, Loader2, X, Plug, ArrowRight } from "lucide-react";
import { PairModal } from "@/pages/Integrations";
import { CreateProjectModal } from "@/components/CreateProjectModal";

const STEPS = [
  { n: 1, label: "Pair a host", icon: Server },
  { n: 2, label: "Connect a provider", icon: Sparkles },
  { n: 3, label: "First project", icon: FolderGit2 },
];

// First-run guided setup. Reuses the existing pair-code flow (PairModal) and the
// create-project form (CreateProjectModal) rather than rebuilding them, and polls
// host/provider status so each step auto-advances the moment its prerequisite is
// true. Never blocks — pairing is async, so we watch GET /hosts on an interval.
export default function OnboardingWizard({ onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [hosts, setHosts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [pairCode, setPairCode] = useState(null);
  const [showPair, setShowPair] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [connecting, setConnecting] = useState(null);

  const hostOnline = useMemo(() => hosts.some((h) => h.online), [hosts]);
  const providerConnected = useMemo(() => providers.some((p) => p.connected), [providers]);

  // Poll host + provider status without blocking; mirrors the 5s poll Integrations
  // uses. skipErrorToast so transient failures during setup stay quiet.
  useEffect(() => {
    let active = true;
    async function poll() {
      try {
        const [h, p] = await Promise.all([
          api.get("/hosts", { skipErrorToast: true }),
          api.get("/providers", { skipErrorToast: true }),
        ]);
        if (!active) return;
        setHosts(h.data.hosts || []);
        setProviders(p.data.providers || []);
      } catch (e) { /* transient — keep polling */ }
    }
    poll();
    const t = setInterval(poll, 3000);
    return () => { active = false; clearInterval(t); };
  }, []);

  // Auto-advance forward only, so a returning user who already finished an earlier
  // step lands on the first thing still missing instead of being pushed backward.
  useEffect(() => {
    if (step === 1 && hostOnline) { setShowPair(false); setStep(2); }
  }, [step, hostOnline]);
  useEffect(() => {
    if (step === 2 && providerConnected) setStep(3);
  }, [step, providerConnected]);

  async function genCode() {
    setGenBusy(true);
    try {
      const { data } = await api.post("/hosts/pair-code");
      setPairCode(data.pairing_code);
      setShowPair(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenBusy(false);
    }
  }

  async function connectProvider(prov) {
    setConnecting(prov);
    try {
      await api.post("/providers/connect", { provider: prov });
      // The status poll flips providerConnected and advances the step.
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConnecting(null);
    }
  }

  const done = { 1: hostOnline, 2: providerConnected, 3: false };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="onboarding-wizard">
        <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl animate-fade-up">
          <div className="flex items-start justify-between mb-1">
            <div>
              <p className="label-caps mb-2">Welcome</p>
              <h3 className="font-heading font-800 text-2xl tracking-tight text-gray-900">Let's get you set up</h3>
            </div>
            <button data-testid="onboarding-skip" onClick={onClose} title="Skip for now"
              className="text-gray-400 hover:text-gray-900 p-1"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-sm text-gray-500 mb-5">Three quick steps and you'll be ready to build.</p>

          {/* step rail */}
          <div className="flex items-center gap-2 mb-6">
            {STEPS.map((s, i) => {
              const active = step === s.n;
              const complete = done[s.n];
              return (
                <div key={s.n} className="flex items-center gap-2 flex-1">
                  <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-700 shrink-0 ${
                    complete ? "bg-emerald-500 text-white" : active ? "bg-black text-white" : "bg-gray-100 text-gray-400"}`}>
                    {complete ? <Check className="w-3.5 h-3.5" /> : s.n}
                  </div>
                  <span className={`text-xs font-600 truncate ${active || complete ? "text-gray-900" : "text-gray-400"}`}>{s.label}</span>
                  {i < STEPS.length - 1 && <div className="h-px flex-1 bg-gray-200" />}
                </div>
              );
            })}
          </div>

          {/* step body */}
          {step === 1 && (
            <div data-testid="onboarding-step-host">
              <div className="flex items-center gap-2 mb-2 text-gray-900"><Server className="w-4.5 h-4.5" /><span className="font-700">Pair your machine</span></div>
              <p className="text-sm text-gray-500 mb-4">Run the Cadence host agent locally so your files and model auth never leave your machine.</p>
              <button data-testid="onboarding-generate-code" onClick={genCode} disabled={genBusy}
                className="w-full bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} {pairCode ? "Show pairing code" : "Generate pairing code"}
              </button>
              {pairCode && (
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-500" data-testid="onboarding-host-waiting">
                  <Loader2 className="w-4 h-4 animate-spin" /> Waiting for your host to come online…
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-provider">
              <div className="flex items-center gap-2 mb-2 text-gray-900"><Sparkles className="w-4.5 h-4.5" /><span className="font-700">Connect a model provider</span></div>
              <p className="text-sm text-gray-500 mb-4">Pick a provider to generate code with. Auth happens on your host via each provider's own CLI.</p>
              <div className="space-y-2">
                {providers.length === 0 && (
                  <div className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading providers…</div>
                )}
                {providers.map((p) => (
                  <div key={p.provider} data-testid={`onboarding-provider-${p.provider}`}
                    className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
                    <div className="min-w-0">
                      <div className="font-600 text-sm text-gray-900 truncate">{p.name}</div>
                      <div className="text-xs text-gray-400 font-mono truncate">{p.auth_hint}</div>
                    </div>
                    {p.connected ? (
                      <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> Connected</span>
                    ) : (
                      <button data-testid={`onboarding-connect-${p.provider}`} onClick={() => connectProvider(p.provider)} disabled={connecting === p.provider}
                        className="text-sm bg-black text-white rounded-lg px-3 py-1.5 font-medium hover:bg-gray-900 disabled:opacity-50 inline-flex items-center gap-2 shrink-0">
                        {connecting === p.provider ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-project">
              <div className="flex items-center gap-2 mb-2 text-gray-900"><FolderGit2 className="w-4.5 h-4.5" /><span className="font-700">Create your first project</span></div>
              <p className="text-sm text-gray-500 mb-4">Spin up a workspace and Cadence will start planning and building right away.</p>
              <button data-testid="onboarding-create-project" onClick={() => setShowCreate(true)}
                className="w-full bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 inline-flex items-center justify-center gap-2">
                Create project <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          <button data-testid="onboarding-skip-link" onClick={onClose}
            className="mt-5 w-full text-center text-xs text-gray-400 hover:text-gray-600">Skip setup for now</button>
        </div>
      </div>

      {showPair && pairCode && <PairModal code={pairCode} onClose={() => setShowPair(false)} />}
      {showCreate && <CreateProjectModal onClose={() => setShowCreate(false)} onCreated={(p) => { setShowCreate(false); onComplete(p); }} />}
    </>
  );
}
